/**
 * A person's face: the photo they uploaded, or the identicon that stands in for one.
 * Prototype: docs/HRMS.jsx line 571-637.
 *
 * The identicon is derived entirely from the FNV-1a hash of their name (`hashStr`
 * in @hrms/shared, shared with the server): background, skin, hair colour, one of
 * four hair styles, facial hair, glasses and one of three mouths. Same name in, same
 * face out, forever — with no network request and nothing stored.
 *
 * The geometry below is copied path-for-path from the prototype. Changing any number
 * here changes every avatar in the product, so don't.
 *
 * A real photo, when there is one, is drawn *over* that identicon rather than
 * instead of it. Three states collapse into one that way: the moment before the
 * image has loaded, the load that fails because the file went missing, and the
 * person who never uploaded anything all show the same generated face instead of an
 * empty circle, a broken-image glyph, or a flash between the two.
 */
import { hashStr } from "@hrms/shared";
import { useEffect, useId, useState, type CSSProperties } from "react";
import "./ui.css";

const SKIN = ["#F2C6A0", "#E5B08A", "#C68642", "#8D5524", "#FFD9B3", "#EAC393"];
const HAIR = ["#2C1B18", "#3D2314", "#1A1A1A", "#5A3825", "#6B4226", "#4A4A4A", "#20232B"];
const AV_BG = ["#EAD94C", "#C9B8FF", "#FF9ED8", "#9BE8C0", "#9AD0F5", "#FFC59B", "#B8E0A0", "#F5B8C4"];

export interface AvatarFaceProps {
  name: string;
  size: number;
}

export function AvatarFace({ name, size }: AvatarFaceProps) {
  const h = hashStr(name || "?");
  const bg = AV_BG[h % AV_BG.length];
  const skin = SKIN[(h >> 3) % SKIN.length];
  const hair = HAIR[(h >> 6) % HAIR.length];
  const hairStyle = (h >> 9) % 4; // 0 short, 1 side, 2 bun/top, 3 long
  const facialHair = (h >> 11) % 3 === 0;
  const glasses = (h >> 13) % 4 === 0;
  const smile = (h >> 15) % 3; // mouth variants
  // Per instance, not per name: the same person's face appears more than once on a
  // page (the rail's identity block and their profile), and a shared id resolves every
  // `url(#…)` to the first copy — which is hidden inside the off-canvas rail on a
  // phone, so the other copies rendered as blank circles.
  const id = `clip${useId().replace(/[^\w-]/g, "")}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      style={{ flexShrink: 0, display: "block" }}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <clipPath id={id}>
          <circle cx="50" cy="50" r="50" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${id})`}>
        <rect width="100" height="100" fill={bg} />
        {/* neck + shoulders */}
        <rect x="38" y="70" width="24" height="20" rx="6" fill={skin} />
        <path d="M20 100 Q50 74 80 100 Z" fill="#ffffff" opacity="0.9" />
        {/* head */}
        <ellipse cx="50" cy="48" rx="22" ry="24" fill={skin} />
        {/* ears */}
        <circle cx="27" cy="50" r="4" fill={skin} />
        <circle cx="73" cy="50" r="4" fill={skin} />
        {/* hair */}
        {hairStyle === 0 && <path d="M28 44 Q30 22 50 22 Q70 22 72 44 Q66 34 50 34 Q34 34 28 44 Z" fill={hair} />}
        {hairStyle === 1 && <path d="M28 46 Q28 22 52 22 Q72 22 72 40 Q60 30 46 32 Q34 34 28 46 Z" fill={hair} />}
        {hairStyle === 2 && (
          <>
            <circle cx="50" cy="22" r="8" fill={hair} />
            <path d="M28 44 Q30 26 50 26 Q70 26 72 44 Q66 36 50 36 Q34 36 28 44 Z" fill={hair} />
          </>
        )}
        {hairStyle === 3 && (
          <path d="M26 46 Q26 20 50 20 Q74 20 74 46 L74 60 Q72 48 68 46 Q66 34 50 34 Q34 34 32 46 Q28 48 26 60 Z" fill={hair} />
        )}
        {/* eyes */}
        <circle cx="42" cy="48" r="2.4" fill="#2B2B2B" />
        <circle cx="58" cy="48" r="2.4" fill="#2B2B2B" />
        {/* eyebrows */}
        <rect x="37" y="42" width="10" height="1.8" rx="0.9" fill={hair} />
        <rect x="53" y="42" width="10" height="1.8" rx="0.9" fill={hair} />
        {/* nose */}
        <path d="M50 50 L48 56 Q50 58 52 56 Z" fill="#00000018" />
        {/* mouth */}
        {smile === 0 && <path d="M43 61 Q50 67 57 61" stroke="#B5503F" strokeWidth="2" fill="none" strokeLinecap="round" />}
        {smile === 1 && <path d="M44 62 Q50 65 56 62" stroke="#B5503F" strokeWidth="2" fill="none" strokeLinecap="round" />}
        {smile === 2 && <ellipse cx="50" cy="62" rx="4" ry="2.5" fill="#B5503F" />}
        {/* facial hair */}
        {facialHair && <path d="M34 56 Q50 74 66 56 Q60 66 50 66 Q40 66 34 56 Z" fill={hair} opacity="0.9" />}
        {/* glasses */}
        {glasses && (
          <g stroke="#2B2B2B" strokeWidth="1.6" fill="none">
            <circle cx="42" cy="48" r="6" />
            <circle cx="58" cy="48" r="6" />
            <line x1="48" y1="48" x2="52" y2="48" />
          </g>
        )}
      </g>
    </svg>
  );
}

export interface AvatarProps {
  name: string;
  size?: number;
  /**
   * The address the API minted for this person's photo, or null/undefined when
   * they have none and the identicon is the answer.
   *
   * Always the server's `photoUrl` — never a path this component assembles. It
   * carries a `?v=` stamped from when the photo was stored, which is what makes a
   * replacement visible immediately in a browser allowed to cache the old one.
   *
   * Optional because not every payload carries one yet. `EmploymentFieldsSchema`
   * does — so the directory, the drawer, the manager's team list and the org chart
   * pass it, and `/me` does for the sidebar — while the dashboard widgets, the
   * leave cards and the claim rows still identify people by name and code alone and
   * therefore keep drawing the identicon. Giving those payloads the field is the
   * way to close the gap; inventing an address from an employee code here is not,
   * because only the server knows the version stamp that makes one safe to cache.
   */
  photoUrl?: string | null;
  /** Colour of the halo ring, e.g. the org chart's status accent. */
  ring?: string;
  className?: string;
  style?: CSSProperties;
}

export function Avatar({ name, size = 32, photoUrl, ring, className, style }: AvatarProps) {
  /**
   * A photo that 404s — deleted between the payload and the render, or a storage
   * mount that went away — must not leave a broken-image glyph in a 28px circle.
   * The identicon underneath is already the right picture for "no photo", so the
   * failure simply stops covering it.
   */
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  useEffect(() => setFailedUrl(null), [photoUrl]);
  const showPhoto = Boolean(photoUrl) && photoUrl !== failedUrl;

  return (
    <div
      className={className}
      title={name}
      style={{
        position: "relative",
        width: size,
        height: size,
        borderRadius: 999,
        overflow: "hidden",
        flexShrink: 0,
        boxShadow: ring ? `0 0 0 2px var(--surface), 0 0 0 3.5px ${ring}` : "none",
        background: "var(--surface)",
        ...style,
      }}
    >
      <AvatarFace name={name} size={size} />
      {showPhoto ? (
        <img
          src={photoUrl ?? undefined}
          // The circle is decoration beside a name that is always rendered next to
          // it; announcing "Photo of Rhea Menon" before the word "Rhea Menon" is
          // one thing said twice.
          alt=""
          aria-hidden="true"
          width={size}
          height={size}
          onError={() => setFailedUrl(photoUrl ?? null)}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            // Photos arrive in whatever aspect a phone took them at; `cover` crops
            // to the circle rather than squashing a face into it.
            objectFit: "cover",
            display: "block",
          }}
        />
      ) : null}
    </div>
  );
}

export default Avatar;
